// Employee notification test - direct HTTP to notifyEmployeesNewOrderHttp
import { createLogger } from './logger';

const logger = createLogger('TestNotificationEmployee');

const EMPLOYEE_FUNCTION_URL =
  process.env.REACT_APP_EMPLOYEE_NOTIFICATION_FUNCTION_URL ||
  'https://us-central1-pack-my-cart-dev.cloudfunctions.net/notifyEmployeesNewOrderHttp';

/**
 * Parse employee notification HTTP response
 */
async function parseEmployeeResponse(response) {
  if (response.ok) {
    const result = await response.json();
    const ok = result?.success === true;
    if (ok) return { success: true, data: result };
    return {
      success: false,
      data: result,
      error: result?.error || result?.message,
      message: result?.message,
      featureFlagDisabled: result?.featureFlagDisabled,
      noEmployees: result?.message?.toLowerCase().includes('no employees')
    };
  }
  const errorText = await response.text();
  return {
    success: false,
    error: `HTTP ${response.status}: ${errorText}`,
    status: response.status
  };
}

/**
 * Test employee endpoint accessibility (OPTIONS / CORS preflight)
 */
export const testEmployeeEndpoint = async () => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const optionsResponse = await fetch(EMPLOYEE_FUNCTION_URL, {
      method: 'OPTIONS',
      headers: {
        Origin: window.location.origin,
        'Access-Control-Request-Method': 'POST',
        'Access-Control-Request-Headers': 'Content-Type'
      },
      mode: 'cors',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    return {
      success: optionsResponse.ok,
      status: optionsResponse.status,
      corsEnabled: optionsResponse.headers.get('Access-Control-Allow-Origin') !== null
    };
  } catch (error) {
    logger.error('Employee endpoint test error:', error);
    return {
      success: false,
      error: error.name === 'AbortError' ? 'Request timeout (10s)' : error.message,
      type: error.name === 'AbortError' ? 'timeout' : 'network_error'
    };
  }
};

/**
 * Test employee notification - POST with test order
 */
export const testEmployeeNotification = async (options = {}) => {
  const { orderId, customerName, supermarketId } = {
    orderId: `test-order-${Date.now()}`,
    customerName: options.customerName || 'Test Customer',
    supermarketId: options.supermarketId || 'test-supermarket',
    ...options
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const payload = { orderId, customerName, supermarketId };

    const response = await fetch(EMPLOYEE_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
      mode: 'cors',
      credentials: 'omit',
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const parsed = await parseEmployeeResponse(response);
    if (parsed.success) {
      logger.info('Employee notification test response:', parsed.data);
    } else {
      logger.warn('Employee notification test failed:', parsed.error || parsed.message, parsed.data);
    }
    return parsed;
  } catch (error) {
    logger.error('Employee notification test error:', error);
    return {
      success: false,
      error: error.name === 'AbortError' ? 'Request timeout (30s)' : error.message,
      type: error.name === 'AbortError' ? 'timeout' : 'network_error'
    };
  }
};
